import { ACCOUNT_NUMBER_MAX_LENGTH, ACCOUNT_NUMBER_MIN_LENGTH, ROUTING_NUMBER_MASK } from '@egw/constants'
import { type TFunction } from '@egw/i18n'
import { removeCurrencySymbols } from '@egw/utils'
import * as yup from 'yup'
import { WireTransferField } from '../../types'

export const buildRecipientInfoFormValidationSchema = ({ t }: { t: TFunction }) => ({
  [WireTransferField.beneficiaryRoutingNumber]: yup
    .string()
    .length(ROUTING_NUMBER_MASK.length, t('wire_transfer.recipient_info.errors.routing_number_min'))
    .required(),
  [WireTransferField.beneficiaryAccountNumber]: yup
    .string()
    .min(ACCOUNT_NUMBER_MIN_LENGTH, t('wire_transfer.recipient_info.errors.beneficiary_account_number_min'))
    .max(ACCOUNT_NUMBER_MAX_LENGTH, t('wire_transfer.recipient_info.errors.beneficiary_account_number_max'))
    .required(),
  [WireTransferField.beneficiaryVerifyAccountNumber]: yup
    .string()
    .required(t('wire_transfer.recipient_info.errors.required_field'))
    .test(
      WireTransferField.beneficiaryVerifyAccountNumber,
      t('wire_transfer.recipient_info.errors.beneficiaryVerifyAccountNumber'),
      (item, context) => context.parent[WireTransferField.beneficiaryAccountNumber] === item,
    ),

  [WireTransferField.line1]: yup
    .string()
    .required(t('wire_transfer.recipient_info.errors.required_field'))
    .max(30, t('wire_transfer.recipient_info.errors.line_1_max')),
  [WireTransferField.city]: yup.string().required(t('wire_transfer.recipient_info.errors.required_field')),
  [WireTransferField.region]: yup.string().required(t('wire_transfer.recipient_info.errors.required_field')),
  [WireTransferField.postalCode]: yup.string().required(t('wire_transfer.recipient_info.errors.required_field')),
  [WireTransferField.intermediaryRoutingNumber]: yup.string().when([WireTransferField.hasIntermediaryBank], {
    is: true,
    then: (schema) =>
      schema.length(ROUTING_NUMBER_MASK.length).required(t('wire_transfer.recipient_info.errors.required_field')),
    otherwise: (schema) => schema.notRequired(),
  }),
  [WireTransferField.beneficiaryFirstName]: yup.string().when([WireTransferField.recipientIsBusiness], {
    is: false,
    then: (schema) => schema.required(t('wire_transfer.recipient_info.errors.required_field')),
    otherwise: (schema) => schema.notRequired(),
  }),
  [WireTransferField.beneficiaryLastName]: yup.string().when([WireTransferField.recipientIsBusiness], {
    is: false,
    then: (schema) => schema.required(t('wire_transfer.recipient_info.errors.required_field')),
    otherwise: (schema) => schema.notRequired(),
  }),
  [WireTransferField.businessName]: yup.string().when([WireTransferField.recipientIsBusiness], {
    is: true,
    then: (schema) => schema.required(t('wire_transfer.recipient_info.errors.required_field')),
    otherwise: (schema) => schema.notRequired(),
  }),
})

export const buildWireTransferValidationSchema = ({
  t,
  availableBalance,
}: {
  t: TFunction
  availableBalance: number
}) => ({
  // ADD MORE VALIDATIONS ON AMOUNT
  [WireTransferField.amount]: yup
    .string()
    .required(t('wire_transfer.recipient_info.errors.required_field'))
    .test(
      WireTransferField.amount,
      t('wire_transfer_review.errors.non_sufficient_funds'),
      (item) => availableBalance >= removeCurrencySymbols(item),
    )
    .test(
      WireTransferField.amount,
      t('wire_transfer_review.errors.amount_is_zero'),
      (item) => removeCurrencySymbols(item) > 0,
    ),
  [WireTransferField.reference]: yup.string().required(t('wire_transfer.recipient_info.errors.required_field')),
  ...buildRecipientInfoFormValidationSchema({ t }),
})

export const recipientInfoFields = [
  WireTransferField.beneficiaryRoutingNumber.toString(),
  WireTransferField.beneficiaryAccountNumber.toString(),
  WireTransferField.beneficiaryVerifyAccountNumber.toString(),
  WireTransferField.beneficiaryFirstName.toString(),
  WireTransferField.beneficiaryLastName.toString(),
  WireTransferField.beneficiaryFirstName.toString(),
  WireTransferField.beneficiaryLastName.toString(),
  WireTransferField.businessName.toString(),
  WireTransferField.line1.toString(),
  WireTransferField.region.toString(),
  WireTransferField.postalCode.toString(),
  WireTransferField.city.toString(),
  WireTransferField.postalCode.toString(),
  WireTransferField.intermediaryRoutingNumber.toString(),
]
